/**
 * Notifications: backend DTOs and API calls for the in-app notification bell.
 *
 * Notifications are addressed to the current user (the token is the identity —
 * no user id in any request). "New" is derived from `clickedAt == null`;
 * opening one stamps it (idempotent server-side), dismissing soft-deletes it.
 * NOTE: the notification-kind field is `kind` on the wire, matching the entity.
 */

import { apiDelete, apiGet, apiPost } from '../apiClient';

export interface NotificationDto {
  id: string;
  /** Notification kind (deadline_due_soon | deadline_overdue | system) — drives treatment. */
  kind: string;
  message: string;
  note: string | null;
  /** The record this notification links through to, if any. */
  targetId: string | null;
  /** The kind of record `targetId` refers to (e.g. "document", "case"). */
  targetType: string | null;
  createdAt: string;
  /** Null while the notification is still "new" (un-opened). */
  clickedAt: string | null;
}

export interface NotificationListResponse {
  notifications: NotificationDto[];
  /** How many are still new (un-clicked) — drives the badge. */
  newCount: number;
}

/** GET /api/notifications — new first, then most recent. */
export function listNotifications(): Promise<NotificationListResponse> {
  return apiGet<NotificationListResponse>('/api/notifications/');
}

/** POST /api/notifications/{id}/click — stamps clickedAt (idempotent). */
export function markNotificationClicked(id: string): Promise<NotificationDto> {
  return apiPost<NotificationDto>(`/api/notifications/${id}/click`);
}

/** DELETE /api/notifications/{id} — the dismiss "x" (soft delete, 204). */
export function dismissNotification(id: string): Promise<void> {
  return apiDelete<void>(`/api/notifications/${id}`);
}
